import { useState, useEffect, useRef, type FormEvent, type KeyboardEvent } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { useAuth, type LoginChallengeResult } from '../contexts/AuthContext'
import { MfaChallengeType } from '../types/enums'
import { groupTotpSecret } from '../lib/cognito'
import { AUTH_CONFIG } from '../constants/config'
import AUResidencyBanner from '../components/ui/AUResidencyBanner'
import { FieldHint } from '../components/ui/FieldHint'

// UX-004: self-service reset lives at /auth/forgot-password (Cognito ForgotPassword).
const FORGOT_PASSWORD_PATH = '/auth/forgot-password'

const CODE_LENGTH = AUTH_CONFIG.MFA_CODE_LENGTH

// Client-side pre-flight for the forced-password-change screen (Story #4722).
// Mirrors the Cognito pool password policy (min length 12; upper, lower, number,
// symbol) so the user gets instant feedback for common mistakes. Cognito remains
// authoritative and its InvalidPasswordException message is surfaced verbatim if
// this pre-check is ever out of step with the pool. Returns a friendly message
// for the first unmet rule, or null when the password satisfies every rule.
const MIN_PASSWORD_LENGTH = 12
function unmetPasswordRules(pw: string): string | null {
  if (pw.length < MIN_PASSWORD_LENGTH) return `Use at least ${MIN_PASSWORD_LENGTH} characters.`
  if (!/[A-Z]/.test(pw)) return 'Include at least one upper-case letter.'
  if (!/[a-z]/.test(pw)) return 'Include at least one lower-case letter.'
  if (!/[0-9]/.test(pw)) return 'Include at least one number.'
  if (!/[^A-Za-z0-9]/.test(pw)) return 'Include at least one symbol.'
  return null
}

// Method-aware copy for the challenge screen (mockup screen 2 ②). The masked
// destination (if any) is shown separately, only after the method is challenged.
function codeInstruction(challenge: string | undefined): string {
  switch (challenge) {
    case MfaChallengeType.SMS_MFA:
      return `Enter the ${CODE_LENGTH}-digit code from the text message we sent you`
    case MfaChallengeType.EMAIL_OTP:
      return `Enter the ${CODE_LENGTH}-digit code we emailed you`
    case MfaChallengeType.SOFTWARE_TOKEN_MFA:
      return `Enter the ${CODE_LENGTH}-digit code from your authenticator app`
    case MfaChallengeType.MFA_SETUP:
      return `Then enter the ${CODE_LENGTH}-digit code it shows to finish signing in`
    default:
      return `Enter your ${CODE_LENGTH}-digit verification code`
  }
}

// "Use another method" fallback rows (mockup screen 2 ③). Pre-selection copy is
// generic ("Text me a code") — the masked destination only appears after the
// method is selected and Cognito issues the code challenge (D6).
function methodActionLabel(method: string): string {
  switch (method) {
    case MfaChallengeType.SMS_MFA:
      return 'Text me a code'
    case MfaChallengeType.EMAIL_OTP:
      return 'Email me a code'
    case MfaChallengeType.SOFTWARE_TOKEN_MFA:
      return 'Use my authenticator app'
    default:
      return 'Use another method'
  }
}

function methodIcon(method: string): string {
  switch (method) {
    case MfaChallengeType.SMS_MFA:
      return '💬'
    case MfaChallengeType.EMAIL_OTP:
      return '✉️'
    case MfaChallengeType.SOFTWARE_TOKEN_MFA:
      return '📱'
    default:
      return '🔐'
  }
}

export default function Login() {
  const { login, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  // UX-006: Signup navigates here with state.registered; sign-up has no email-verification gate.
  const justRegistered = (location.state as { registered?: boolean } | null)?.registered === true
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  // MFA challenge state (mockup screen 2).
  const [mfaRequired, setMfaRequired] = useState(false)
  // Story #4722 — forced password change (FORCE_CHANGE_PASSWORD / NEW_PASSWORD_REQUIRED).
  const [newPasswordRequired, setNewPasswordRequired] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [mfaChallenge, setMfaChallenge] = useState<string | undefined>(undefined)
  const [availableMethods, setAvailableMethods] = useState<string[]>([])
  const [mfaDestination, setMfaDestination] = useState<string | undefined>(undefined)
  const [codeDigits, setCodeDigits] = useState<string[]>(() => Array(CODE_LENGTH).fill(''))
  const [rememberDevice, setRememberDevice] = useState(false)
  // Story #4709 — MFA_SETUP enrolment (Cognito pool ON, zero devices yet).
  const [setupSecretCode, setSetupSecretCode] = useState<string | undefined>(undefined)
  const [setupOtpauthUri, setSetupOtpauthUri] = useState<string | undefined>(undefined)
  const inputsRef = useRef<Array<HTMLInputElement | null>>([])

  // Runtime feature flags from config.js (MFA plan D18). Read defensively — the
  // same pattern useAuthedFetch uses — so a config that predates Phase 0 (no
  // MFA_METHODS / MFA_REMEMBER_DEVICE keys) reads as: recovery hidden, remember
  // off. The recovery link is gated so it is NEVER a dead control before the
  // Phase-2 recovery flow ships.
  const cfg = typeof window !== 'undefined' ? window.INNOVATEFLOW_CONFIG : undefined
  const enabledMfaMethods = (cfg?.MFA_METHODS ?? '')
    .split(',')
    .map((m) => m.trim().toLowerCase())
    .filter(Boolean)
  const recoveryEnabled = enabledMfaMethods.includes('recovery')
  const rememberDeviceEnabled = cfg?.MFA_REMEMBER_DEVICE === true

  const isSelectChallenge = mfaChallenge === MfaChallengeType.SELECT_MFA_TYPE
  const isSetupChallenge = mfaChallenge === MfaChallengeType.MFA_SETUP
  // Fallback list = every offered method except the one currently being answered.
  const fallbackMethods = availableMethods.filter((m) => m !== mfaChallenge)

  // Focus the first code box whenever a direct code challenge appears.
  useEffect(() => {
    if (mfaRequired && mfaChallenge !== MfaChallengeType.SELECT_MFA_TYPE) {
      inputsRef.current[0]?.focus()
    }
  }, [mfaRequired, mfaChallenge])

  // UX-044: redirect declaratively. Calling navigate() during render is a
  // side effect in render and warns under React 18.
  if (isAuthenticated) {
    return <Navigate to="/" replace />
  }

  const resetMfaState = () => {
    setMfaRequired(false)
    setMfaChallenge(undefined)
    setAvailableMethods([])
    setMfaDestination(undefined)
    setCodeDigits(Array(CODE_LENGTH).fill(''))
    setRememberDevice(false)
    setSetupSecretCode(undefined)
    setSetupOtpauthUri(undefined)
    setNewPasswordRequired(false)
    setNewPassword('')
    setConfirmPassword('')
    setShowNewPassword(false)
  }

  const applyLoginResult = (result: LoginChallengeResult) => {
    if (result.newPasswordRequired) {
      // Forced password change: hold on a dedicated screen that collects a
      // genuinely new password. Never auto-advance.
      setMfaRequired(false)
      setNewPasswordRequired(true)
      setError('')
      return
    }
    if (result.mfaRequired) {
      setNewPasswordRequired(false)
      setMfaRequired(true)
      setMfaChallenge(result.challengeName)
      // Preserve the known method set across a selection: Cognito only returns
      // MFAS_CAN_CHOOSE on the SELECT_MFA_TYPE step, not on the code challenge it
      // then issues — so keep the earlier list so the "use another method"
      // fallbacks stay available after the user picks one.
      if (result.availableMethods) setAvailableMethods(result.availableMethods)
      setMfaDestination(result.destination)
      setCodeDigits(Array(CODE_LENGTH).fill(''))
      setSetupSecretCode(result.secretCode)
      setSetupOtpauthUri(result.otpauthUri)
    } else {
      resetMfaState()
      navigate('/')
    }
  }

  // Password step (mockup screen 1 ②, unchanged) → InitiateAuth.
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      // Read straight from the form, not React state: browser/password-manager autofill
      // can populate the inputs without firing onChange, leaving state stale and forcing
      // a retype / second click. FormData reflects what's actually in the fields.
      const fd = new FormData(e.currentTarget as HTMLFormElement)
      const emailVal = ((fd.get('email') as string) ?? email).trim()
      const passwordVal = (fd.get('password') as string) ?? password
      // Keep state in sync so the controlled inputs show the values that were submitted.
      if (emailVal !== email) setEmail(emailVal)
      if (passwordVal !== password) setPassword(passwordVal)
      const result = await login(emailVal, passwordVal, undefined)
      applyLoginResult(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  // Answer a surfaced NEW_PASSWORD_REQUIRED challenge with a genuinely new
  // password. Cognito is the authority on the policy and returns a clear
  // InvalidPasswordException message we surface; the client-side checks below
  // are a fast pre-flight mirroring the pool policy (min 12, upper/lower/number/
  // symbol) so the user gets immediate feedback for the common mistakes.
  const submitNewPassword = async (e: FormEvent) => {
    e.preventDefault()
    if (loading) return
    setError('')
    if (newPassword !== confirmPassword) {
      setError('The two passwords do not match.')
      return
    }
    if (newPassword === password) {
      setError('Choose a new password that is different from your temporary one.')
      return
    }
    const unmet = unmetPasswordRules(newPassword)
    if (unmet) {
      setError(unmet)
      return
    }
    setLoading(true)
    try {
      // Answer with the entered NEW password; `password` still holds the
      // temporary one, which login() needs for the challenge Session context.
      const result = await login(email, password, undefined, { newPassword })
      applyLoginResult(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not set your new password')
    } finally {
      setLoading(false)
    }
  }

  // Answer the pending direct code challenge (TOTP/SMS/email) with the code.
  const submitCode = async (code: string) => {
    if (loading) return
    setError('')
    setLoading(true)
    try {
      const result = await login(email, password, code)
      applyLoginResult(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed')
      // Clear for a clean resubmit (the session is still valid server-side).
      setCodeDigits(Array(CODE_LENGTH).fill(''))
      inputsRef.current[0]?.focus()
    } finally {
      setLoading(false)
    }
  }

  // Choose a method from the SELECT_MFA_TYPE picker (or the fallback list).
  const selectMethod = async (method: string) => {
    if (loading) return
    setError('')
    setLoading(true)
    try {
      const result = await login(email, password, undefined, { selectMethod: method })
      applyLoginResult(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not switch method')
    } finally {
      setLoading(false)
    }
  }

  const handleMfaSubmit = (e: FormEvent) => {
    e.preventDefault()
    const code = codeDigits.join('')
    if (code.length < CODE_LENGTH) {
      setError(`Enter all ${CODE_LENGTH} digits`)
      return
    }
    void submitCode(code)
  }

  const handleDigitChange = (index: number, rawValue: string) => {
    const digits = rawValue.replace(/\D/g, '')
    const next = [...codeDigits]
    if (!digits) {
      // Deletion / non-numeric input → clear this box.
      next[index] = ''
      setCodeDigits(next)
      return
    }
    if (digits.length > 1) {
      // Paste (or fast type) → distribute across the boxes from here.
      for (let i = 0; i < digits.length && index + i < CODE_LENGTH; i++) {
        next[index + i] = digits[i]
      }
    } else {
      next[index] = digits
    }
    setCodeDigits(next)
    // Advance focus to the box after the last one we just filled.
    const lastTouched = Math.min(index + Math.max(digits.length, 1) - 1, CODE_LENGTH - 1)
    inputsRef.current[Math.min(lastTouched + 1, CODE_LENGTH - 1)]?.focus()
    // Auto-submit once every box is filled.
    if (next.every((d) => d !== '')) {
      void submitCode(next.join(''))
    }
  }

  const handleDigitKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !codeDigits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus()
    }
  }

  const inputClass =
    'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-iflow-500 focus:ring-2 focus:ring-iflow-200'

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-sky-50 via-white to-[#E8EFF4]">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="InnovateFlow" className="w-20 h-20 rounded-2xl mx-auto mb-4 shadow-lg" />
          <h1 className="text-2xl font-bold text-iflow-900">InnovateFlow</h1>
          <p className="text-sm text-slate-500 mt-1">Secure innovation management</p>
        </div>

        <div className="bg-white rounded-2xl border border-[rgba(12,74,110,0.12)] p-8 shadow-xl">
          <h2 className="text-lg font-semibold text-iflow-900">
            {newPasswordRequired
              ? 'Set a new password'
              : mfaRequired
                ? (isSetupChallenge ? 'Set up two-factor authentication' : 'Two-factor verification')
                : 'Sign in to your workspace'}
          </h2>
          <p className="text-sm text-slate-500 mt-1 mb-6">
            {newPasswordRequired ? (
              <>
                Your temporary password must be changed before you continue —{' '}
                <span className="font-medium text-slate-700">{email || 'your account'}</span>
              </>
            ) : mfaRequired ? (
              <>
                Signed in as <span className="font-medium text-slate-700">{email || 'your account'}</span>
              </>
            ) : (
              'Welcome back — pick up where you left off.'
            )}
          </p>

          {error && (
            <div
              role="alert"
              className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"
            >
              {error}
            </div>
          )}

          {newPasswordRequired ? (
            /* ---------- Forced password change (Story #4722) ---------- */
            <form onSubmit={submitNewPassword} className="space-y-4">
              <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-slate-700 mb-1">New password</label>
                <div className="relative">
                  <input
                    id="new-password"
                    name="new-password"
                    type={showNewPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={`${inputClass} pr-10`}
                    placeholder="Choose a new password"
                    aria-describedby="new-password-hint"
                    required
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <FieldHint id="new-password-hint">
                  At least {MIN_PASSWORD_LENGTH} characters, with upper- and lower-case letters, a number, and a symbol.
                </FieldHint>
              </div>
              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-slate-700 mb-1">Confirm new password</label>
                <input
                  id="confirm-password"
                  name="confirm-password"
                  type={showNewPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={inputClass}
                  placeholder="Re-enter your new password"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 bg-iflow-600 text-white text-sm font-semibold rounded-lg hover:bg-iflow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Setting your password...' : 'Set password & continue'}
              </button>
            </form>
          ) : !mfaRequired ? (
            /* ---------- Password step (screen 1) ---------- */
            <form onSubmit={handleSubmit} className="space-y-4">
              {justRegistered && (
                <div role="status" className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">
                  Account created. You can sign in now.
                </div>
              )}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClass}
                  placeholder="Enter your email"
                  aria-describedby="email-hint"
                  required
                />
                <FieldHint id="email-hint">Use the email your inventions are registered under.</FieldHint>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="password" className="block text-sm font-medium text-slate-700">Password</label>
                  <Link
                    to={FORGOT_PASSWORD_PATH}
                    className="text-sm font-medium text-iflow-600 hover:text-iflow-700"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`${inputClass} pr-10`}
                    placeholder="Enter your password"
                    aria-describedby="password-hint"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <FieldHint id="password-hint">We never store your password in plain text.</FieldHint>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 bg-iflow-700 text-white text-sm font-medium rounded-lg hover:bg-iflow-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>

              <p className="text-center text-sm text-slate-500">
                New to InnovateFlow?{' '}
                <button type="button" onClick={() => navigate('/signup')} className="text-iflow-600 hover:text-iflow-700 font-medium">
                  Create an account &rarr;
                </button>
              </p>
              <p className="text-center text-sm text-slate-600">
                <Link to="/welcome" className="text-iflow-600 hover:text-iflow-700 font-medium">&larr; Back to welcome</Link>
              </p>
            </form>
          ) : isSelectChallenge ? (
            /* ---------- Method chooser (Cognito SELECT_MFA_TYPE, >1 method) ---------- */
            <div className="space-y-4">
              <p className="text-sm text-slate-600">Choose how you'd like to verify this sign-in.</p>
              <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 text-sm">
                {availableMethods.map((m) => (
                  <button
                    key={m}
                    type="button"
                    disabled={loading}
                    onClick={() => void selectMethod(m)}
                    className="w-full px-3 py-3 text-left text-iflow-700 hover:bg-iflow-50 disabled:opacity-50 flex items-center gap-2 first:rounded-t-lg last:rounded-b-lg"
                  >
                    <span aria-hidden>{methodIcon(m)}</span> <span>{methodActionLabel(m)}</span>
                  </button>
                ))}
              </div>
              {recoveryEnabled && (
                <div className="pt-4 mt-2 border-t border-slate-100 text-center">
                  <button
                    type="button"
                    onClick={() => navigate('/auth/recovery')}
                    className="text-xs text-slate-500 hover:text-iflow-700 underline"
                  >
                    🔑 Lost access to all your methods? Account recovery &rarr;
                  </button>
                  <p className="text-[11px] text-slate-400 mt-1">
                    Recovery uses a separate, email-confirmed flow — it removes your second factor rather than signing you in here.
                  </p>
                </div>
              )}
            </div>
          ) : isSetupChallenge ? (
            /* ---------- First-time enrolment (Cognito MFA_SETUP — no device yet, Story #4709) ---------- */
            <form onSubmit={handleMfaSubmit} className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-iflow-900 mb-1">Scan with your authenticator app</h3>
                <p className="text-xs text-slate-500 mb-3">Your organisation requires a second factor. Set one up now to finish signing in.</p>
                <div className="flex justify-center mb-3">
                  <div className="p-3 bg-white border border-slate-200 rounded-xl">
                    {setupOtpauthUri
                      ? <QRCodeSVG value={setupOtpauthUri} size={148} level="M" />
                      : <div className="w-[148px] h-[148px] flex items-center justify-center text-slate-300"><Loader2 className="animate-spin" /></div>}
                  </div>
                </div>
                {setupSecretCode && (
                  <div className="text-center mb-4">
                    <p className="text-xs text-slate-400">Can&apos;t scan? Enter this key manually:</p>
                    <code className="text-xs font-mono text-iflow-800 bg-iflow-50 border border-iflow-100 rounded px-2 py-1 inline-block mt-1 tracking-wider">
                      {groupTotpSecret(setupSecretCode)}
                    </code>
                  </div>
                )}
              </div>

              <div>
                <label id="mfa-code-label" className="block text-sm font-semibold text-iflow-900 mb-1">
                  {codeInstruction(mfaChallenge)}
                </label>
                <div role="group" aria-labelledby="mfa-code-label" className="flex gap-2 justify-center my-3">
                  {codeDigits.map((digit, i) => (
                    <input
                      key={i}
                      ref={(el) => { inputsRef.current[i] = el }}
                      type="text"
                      inputMode="numeric"
                      autoComplete={i === 0 ? 'one-time-code' : 'off'}
                      aria-label={`Digit ${i + 1} of ${CODE_LENGTH}`}
                      value={digit}
                      onChange={(e) => handleDigitChange(i, e.target.value)}
                      onKeyDown={(e) => handleDigitKeyDown(i, e)}
                      onFocus={(e) => e.target.select()}
                      className="w-11 h-12 text-center text-lg font-bold rounded-lg border border-slate-300 text-slate-900 outline-none focus:border-iflow-500 focus:ring-2 focus:ring-iflow-200"
                    />
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 bg-iflow-600 text-white text-sm font-semibold rounded-lg hover:bg-iflow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Verifying...' : 'Verify & finish sign-in'}
              </button>
            </form>
          ) : (
            /* ---------- Code challenge (screen 2) ---------- */
            <form onSubmit={handleMfaSubmit} className="space-y-4">
              <div>
                <label id="mfa-code-label" className="block text-sm font-semibold text-iflow-900 mb-1">
                  {codeInstruction(mfaChallenge)}
                </label>
                {mfaDestination && (
                  <p className="text-xs text-slate-500 mb-2">Sent to {mfaDestination}</p>
                )}
                <div role="group" aria-labelledby="mfa-code-label" className="flex gap-2 justify-center my-3">
                  {codeDigits.map((digit, i) => (
                    <input
                      key={i}
                      ref={(el) => { inputsRef.current[i] = el }}
                      type="text"
                      inputMode="numeric"
                      autoComplete={i === 0 ? 'one-time-code' : 'off'}
                      aria-label={`Digit ${i + 1} of ${CODE_LENGTH}`}
                      value={digit}
                      onChange={(e) => handleDigitChange(i, e.target.value)}
                      onKeyDown={(e) => handleDigitKeyDown(i, e)}
                      onFocus={(e) => e.target.select()}
                      className="w-11 h-12 text-center text-lg font-bold rounded-lg border border-slate-300 text-slate-900 outline-none focus:border-iflow-500 focus:ring-2 focus:ring-iflow-200"
                    />
                  ))}
                </div>
              </div>

              {rememberDeviceEnabled && (
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={rememberDevice}
                    onChange={(e) => setRememberDevice(e.target.checked)}
                    className="rounded border-slate-300 text-iflow-600"
                  />
                  Remember this device for 30 days
                </label>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 bg-iflow-600 text-white text-sm font-semibold rounded-lg hover:bg-iflow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>

              {fallbackMethods.length > 0 && (
                <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 text-sm">
                  <div className="px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 rounded-t-lg">
                    Having trouble? Use another method
                  </div>
                  {fallbackMethods.map((m) => (
                    <button
                      key={m}
                      type="button"
                      disabled={loading}
                      onClick={() => void selectMethod(m)}
                      className="w-full px-3 py-2.5 text-left text-iflow-700 hover:bg-iflow-50 disabled:opacity-50 flex items-center gap-2 last:rounded-b-lg"
                    >
                      <span aria-hidden>{methodIcon(m)}</span> <span>{methodActionLabel(m)}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Account recovery — visually separated from the inline code methods
                  above (D6/R16). It is NOT an inline peer: a distinct, bordered
                  block that navigates to the separate email-confirmed /auth/recovery
                  flow, never accepting a recovery code here. Gated on MFA_METHODS
                  including "recovery" so it is never a dead control. */}
              {recoveryEnabled && (
                <div className="pt-4 mt-2 border-t border-slate-100 text-center">
                  <button
                    type="button"
                    onClick={() => navigate('/auth/recovery')}
                    className="text-xs text-slate-500 hover:text-iflow-700 underline"
                  >
                    🔑 Lost access to all your methods? Account recovery &rarr;
                  </button>
                  <p className="text-[11px] text-slate-400 mt-1">
                    Recovery uses a separate, email-confirmed flow — it removes your second factor rather than signing you in here.
                  </p>
                </div>
              )}
            </form>
          )}

          <div className="mt-6 pt-6 border-t border-slate-100">
            <AUResidencyBanner />
          </div>
        </div>
      </div>
    </div>
  )
}
